import React, { useState, useEffect, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { reservationApi } from '../reservation_api';
import { Court } from '../api_types';
import './styles/CourtGridView.css';
import { 
  CalendarIcon, 
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { ErrorHandler } from '../errors';
import { useQueryClient } from '@tanstack/react-query';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface TimeSlotOption {
  startTime: string;
  duration: '30min' | '1hour';
  isRecommended: boolean;
}

interface SmartRecommendation {
  startTime: string;
  endTime: string;
  availableCourts: {
    courtId: string;
    courtName: string;
  }[];
  startTimeMinutes?: number;
}

interface BusinessHours {
  [day: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
}

interface ConflictCheckResult {
  hasConflict: boolean;
  conflictMessage?: string;
}

interface ScheduleSlot {
  id: string;
  courtId: string;
  date: string;
  startTime: string;
  endTime: string;
  status: 'available' | 'booked' | 'blocked';
  notes?: string;
  assigned_to?: string | null;
  assigned_user_details?: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;
  isEvent?: boolean;
  eventTitle?: string;
}

interface NewCourtReservationModalProps {
  open: boolean;
  onClose: () => void;
  courtId?: string;
  date: Date;
  startTime?: string;
  endTime?: string;
  courts: Court[];
  assignableUsers: {
    id: string;
    name: string;
    email: string;
    role: string;
    type: 'business' | 'staff' | 'member';
  }[];
  existingReservation?: {
    id: string;
    courtId: string;
    date: string;
    startTime: string;
    endTime: string;
    status: 'available' | 'booked' | 'blocked';
    notes?: string;
    assignedTo?: string | null;
    assignedUserDetails?: {
      id: string;
      name: string;
      email: string;
      role: string;
    } | null;
    isEvent?: boolean;
    eventTitle?: string;
  } | null;
  onSave: () => Promise<void>;
  recommendedTimeSlots?: TimeSlotOption[];
  selectedDuration?: '30min' | '1hour';
  onDurationChange?: (duration: '30min' | '1hour') => void;
  selectedUser?: string;
  businessHours: BusinessHours;
  showAssignTo?: boolean;
  onDateChange?: (date: Date) => void;
  businessId: string;
}

const DateSelector: React.FC<{
  date: Date;
  onDateChange: (date: Date) => void;
  businessHours: BusinessHours;
}> = ({ date, onDateChange, businessHours }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(date.getFullYear(), date.getMonth(), 1));

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return eachDayOfInterval({ start, end });
  }, [calendarMonth]);

  const handleDateSelect = (newDate: Date) => {
    onDateChange(newDate);
    setShowCalendar(false);
  };

  const isDateAvailable = (day: Date) => {
    const dayOfWeek = format(day, 'EEEE').toLowerCase();
    return !businessHours[dayOfWeek as keyof BusinessHours]?.isClosed;
  };

  return (
    <div className="relative">
      <div 
        className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:border-indigo-500 cursor-pointer transition-colors"
        onClick={() => setShowCalendar(!showCalendar)}
      >
        <CalendarIcon className="h-5 w-5 text-gray-500" />
        <span className="text-gray-700">{format(date, 'EEEE, MMMM d, yyyy')}</span>
      </div>

      {showCalendar && (
        <div className="absolute z-10 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-72">
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              &#8592;
            </button>
            <h3 className="font-medium">{format(calendarMonth, 'MMMM yyyy')}</h3>
            <button 
              onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              &#8594;
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay() }).map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square" />
            ))}

            {calendarDays.map(day => {
              const isAvailable = isDateAvailable(day);
              const isSelected = format(day, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => isAvailable && handleDateSelect(day)}
                  className={`
                    aspect-square rounded-full text-sm
                    ${isSelected ? 'bg-indigo-600 text-white' : ''}
                    ${!isSelected && isAvailable ? 'hover:bg-gray-100' : ''}
                    ${!isAvailable ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700'}
                  `}
                  disabled={!isAvailable}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex justify-center">
            <button
              onClick={() => handleDateSelect(new Date())}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const checkConflictsUsingCache = (
  schedules: ScheduleSlot[],
  courtId: string,
  date: string,
  startTime: string,
  endTime: string,
  excludeEventId?: string
): boolean => {
  // Convert times to minutes for easier comparison
  const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
  const endMinutes = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1]);

  return schedules.some(slot => {
    // Skip if it's the event we're excluding
    if (excludeEventId && slot.id === excludeEventId) return false;

    // Skip if it's not the same court or date
    if (slot.courtId !== courtId || slot.date !== date) return false;

    // Convert slot times to minutes
    const slotStartMinutes = parseInt(slot.startTime.split(':')[0]) * 60 + parseInt(slot.startTime.split(':')[1]);
    const slotEndMinutes = parseInt(slot.endTime.split(':')[0]) * 60 + parseInt(slot.endTime.split(':')[1]);

    // Check for overlap
    return (
      (startMinutes >= slotStartMinutes && startMinutes < slotEndMinutes) || // Start time falls within existing slot
      (endMinutes > slotStartMinutes && endMinutes <= slotEndMinutes) || // End time falls within existing slot
      (startMinutes <= slotStartMinutes && endMinutes >= slotEndMinutes) // New slot completely encompasses existing slot
    );
  });
};

const NewCourtReservationModal: React.FC<NewCourtReservationModalProps> = ({
  open,
  onClose,
  courtId,
  date,
  startTime,
  endTime,
  courts,
  assignableUsers,
  existingReservation,
  onSave,
  recommendedTimeSlots = [],
  selectedDuration = '1hour',
  onDurationChange,
  selectedUser = '',
  businessHours,
  showAssignTo = true,
  onDateChange,
  businessId,
}) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<'available' | 'booked' | 'blocked'>('booked');
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(
    courtId ? courts.find(c => c.id === courtId) || null : null
  );
  const [selectedUserState, setSelectedUserState] = useState<string>(selectedUser);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCourtId, setLoadingCourtId] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | undefined>(startTime);
  const [selectedEndTime, setSelectedEndTime] = useState<string | undefined>(endTime);
  const [currentDuration, setCurrentDuration] = useState<'30min' | '1hour'>(selectedDuration || '1hour');
  const [manualStartTime, setManualStartTime] = useState<string>('');
  const [manualEndTime, setManualEndTime] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'manual' | 'smart'>(existingReservation ? 'manual' : 'smart');
  const [preferredTime, setPreferredTime] = useState<string>('');
  const [recommendations, setRecommendations] = useState<SmartRecommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [smartBookingDuration, setSmartBookingDuration] = useState<'30min' | '1hour'>('1hour');
  const [showCourtSelection, setShowCourtSelection] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    startTime: string;
    endTime: string;
    availableCourts: {
      courtId: string;
      courtName: string;
    }[];
  } | null>(null);
  const [conflict, setConflict] = useState<ConflictCheckResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [smartSelectedDate, setSmartSelectedDate] = useState<Date>(date);
  const queryClient = useQueryClient();

  // Get the day of the week for the selected date
  const selectedDay = format(date, 'EEEE').toLowerCase();
  const currentBusinessHours = businessHours[selectedDay];

  // Add memoization for time slot calculations
  const calculateTimeSlots = useMemo(() => {
    if (!currentBusinessHours || currentBusinessHours.isClosed) return [];

    const [openHour, openMinute] = currentBusinessHours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = currentBusinessHours.closeTime.split(':').map(Number);
    const openTimeMinutes = openHour * 60 + openMinute;
    const closeTimeMinutes = closeHour * 60 + closeMinute;

    const slots = [];
    for (let minutes = openTimeMinutes; minutes < closeTimeMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
    return slots;
  }, [currentBusinessHours]);

  // Add caching for conflict checks
  const conflictCheckCache = useMemo(() => new Map<string, boolean>(), []);

  useEffect(() => {
    if (existingReservation) {
      setStatus(existingReservation.status);
      setSelectedUserState(existingReservation.assignedTo || '');
      setNotes(existingReservation.notes || '');
      setManualStartTime(existingReservation.startTime);
      setManualEndTime(existingReservation.endTime);
      const court = courts.find(c => c.id === existingReservation.courtId);
      setSelectedCourt(court || null);

      // Calculate duration based on start and end times
      if (existingReservation.startTime && existingReservation.endTime) {
        const duration = calculateDuration(existingReservation.startTime, existingReservation.endTime);
        setCurrentDuration(duration);
      }
    } else {
      setStatus('booked');
      setSelectedUserState(selectedUser);
      setNotes('');
      setManualStartTime(startTime || '');
      setManualEndTime(endTime || '');
      setCurrentDuration(selectedDuration);

      if (courtId) {
        const court = courts.find(c => c.id === courtId);
        setSelectedCourt(court || null);
      }
    }
  }, [existingReservation, startTime, endTime, selectedDuration, courts, courtId, selectedUser]);

  // Real-time conflict checking effect
  useEffect(() => {
    let isMounted = true;
    async function checkConflict() {
      if (selectedCourt && manualStartTime && manualEndTime) {
        const result = await reservationApi.checkTimeConflicts(
          businessId,
          selectedCourt.facilityId,
          {
            courtId: selectedCourt.id,
            date: format(date, 'yyyy-MM-dd'),
            startTime: manualStartTime,
            endTime: manualEndTime,
            excludeReservationId: existingReservation?.id,
            checkOneTimeEvents: true,
            checkRecurringEvents: true,
            checkReservations: true,
            checkClasses: true
          }
        );
        if (isMounted) setConflict(result.hasConflict ? result : null);
      } else {
        if (isMounted) setConflict(null);
      }
    }
    checkConflict();
    return () => { isMounted = false; };
  }, [selectedCourt, manualStartTime, manualEndTime, date, existingReservation]);

  // Generate time options based on business hours
  const timeOptions = useMemo(() => {
    if (!currentBusinessHours || currentBusinessHours.isClosed) {
      return [];
    }

    const options = [];
    const [openHour, openMinute] = currentBusinessHours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = currentBusinessHours.closeTime.split(':').map(Number);

    const openTimeMinutes = openHour * 60 + openMinute;
    const closeTimeMinutes = closeHour * 60 + closeMinute;

    for (let minutes = openTimeMinutes; minutes < closeTimeMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(time);
    }

    return options;
  }, [currentBusinessHours]);

  const formatTimeToAMPM = (time: string | undefined) => {
    if (!time) return 'Not set';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  const calculateEndTime = (startTime: string, duration: '30min' | '1hour') => {
    const [hours, minutes] = startTime.split(':').map(Number);
    let durationInMinutes = 60; // Default to 1 hour
    if (duration === '30min') {
      durationInMinutes = 30;
    }

    const endDate = new Date();
    endDate.setHours(hours, minutes + durationInMinutes);
    return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
  };

  const handleDurationChange = (duration: '30min' | '1hour') => {
    setCurrentDuration(duration);
    onDurationChange?.(duration);

    // Only update end time if we have a start time
    if (manualStartTime) {
      const newEndTime = calculateEndTime(manualStartTime, duration);
      setManualEndTime(newEndTime);
    }
  };

  const handleTimeSlotSelect = (time: string) => {
    const endTime = calculateEndTime(time, currentDuration);
    setManualStartTime(time);
    setManualEndTime(endTime);
    setSelectedStartTime(time);
    setSelectedEndTime(endTime);
  };

  const handleManualTimeChange = (time: string, type: 'start' | 'end') => {
    if (type === 'start') {
      setManualStartTime(time);
      // Only update end time if it's not set or if the new start time is after current end time
      if (!manualEndTime || time > manualEndTime) {
        const newEndTime = calculateEndTime(time, currentDuration);
        setManualEndTime(newEndTime);
      }
      // Calculate duration based on start and end times if end time exists
      if (manualEndTime) {
        const duration = calculateDuration(time, manualEndTime);
        setCurrentDuration(duration);
        onDurationChange?.(duration);
      }
    } else {
      setManualEndTime(time);
      // Only update start time if it's not set or if the new end time is before current start time
      if (!manualStartTime || time < manualStartTime) {
        setManualStartTime(time);
      }
      // Calculate duration based on start and end times if start time exists
      if (manualStartTime) {
        const duration = calculateDuration(manualStartTime, time);
        setCurrentDuration(duration);
        onDurationChange?.(duration);
      }
    }
    // Clear recommended time selection when manually changing times
    setSelectedStartTime(undefined);
    setSelectedEndTime(undefined);
  };

  const calculateDuration = (startTime: string, endTime: string): '30min' | '1hour' => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    const durationMinutes = endTotalMinutes - startTotalMinutes;

    if (durationMinutes === 30) return '30min';
    return '1hour';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourt?.id) {
      setErrorMessage('Please select a court');
      return;
    }

      // Use manual times if they're set, otherwise use recommended times
      const finalStartTime = manualStartTime || selectedStartTime;
      const finalEndTime = manualEndTime || selectedEndTime;

      if (!finalStartTime || !finalEndTime) {
        setErrorMessage('Please select a time slot');
        return;
      }

    setLoading(true);
    setErrorMessage(null);
    try {
      // Always include assigned_user_details: if no user is selected, use current business user
      let selectedUserId = selectedUserState || user?.uid;
      let selectedUserDetails = assignableUsers.find(u => u.id === selectedUserId);

      // If we have a selected user but no details, try to find them in assignableUsers
      if (!selectedUserDetails && selectedUserId) {
        selectedUserDetails = assignableUsers.find(u => u.id === selectedUserId);
      }

      // If still no details and we have a user object, create from user object
      if (!selectedUserDetails && user) {
        selectedUserDetails = {
          id: user.uid,
          name: user.name || user.email || '',
          email: user.email || '',
          role: user.role || 'business',
          type: 'business',
        };
      }

      const reservationData = {
        court_id: selectedCourt.id,
        date: format(activeTab === 'smart' ? smartSelectedDate : date, 'yyyy-MM-dd'),
        start_time: finalStartTime,
        end_time: finalEndTime,
        status,
        notes,
        assigned_to: selectedUserId,
        assigned_user_details: selectedUserDetails,
        exclude_event_id: existingReservation?.id,
        created_at: new Date(),
        updated_at: new Date()
      };

      if (existingReservation) {
        // Update existing reservation
        await reservationApi.updateReservation(businessId, selectedCourt.facilityId, existingReservation.id, reservationData);
      } else {
        // Create new reservation
        await reservationApi.createReservation(businessId, selectedCourt.facilityId, reservationData);
      }

      await onSave();
      onClose();
    } catch (error: any) {
      const apiError = ErrorHandler.handle(error);
      let errorMsg = '';
      if (apiError.error?.message) {
        errorMsg = apiError.error.message;
      } else if (apiError.error?.details) {
        errorMsg = typeof apiError.error.details === 'string'
          ? apiError.error.details
          : JSON.stringify(apiError.error.details);
      }

      setErrorMessage(errorMsg);
      // Also set the conflict state to display the error message
      setConflict({
        hasConflict: true,
        conflictMessage: errorMsg
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!existingReservation || !window.confirm('Are you sure you want to delete this reservation?')) {
      return;
    }

    setLoading(true);
    try {
      await reservationApi.deleteReservation(businessId, selectedCourt?.facilityId || '', existingReservation.id);
      await onSave();
      onClose();
    } catch (error) {
      console.error('Error deleting reservation:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update recommendations when smartSelectedDate changes
  useEffect(() => {
    if (activeTab === 'smart' && preferredTime) {
      findSmartRecommendations(preferredTime, smartSelectedDate);
    }
    // eslint-disable-next-line
  }, [smartSelectedDate]);

  // Update smartSelectedDate if modal is opened with a new date
  useEffect(() => {
    setSmartSelectedDate(date);
  }, [date, open]);

  // Replace the findSmartRecommendations function with this optimized version
  const findSmartRecommendations = async (preferredTime: string, customDate?: Date) => {
    const useDate = customDate || smartSelectedDate;
    if (!preferredTime || !courts.length || !currentBusinessHours || currentBusinessHours.isClosed) return;

    const facilityId = courts[0]?.facilityId;
    if (!facilityId) {
      console.error('No facility ID found');
      return;
    }

    setLoadingRecommendations(true);
    try {
      const dateStr = format(useDate, 'yyyy-MM-dd');
      const recommendations: SmartRecommendation[] = [];
      const exactTimeRecommendations: SmartRecommendation[] = [];

      // Get cached schedules from React Query
      const cachedSchedules = queryClient.getQueryData<ScheduleSlot[]>(['schedules', facilityId, dateStr]) || [];

      // First check for exact time match
      const [preferredHour, preferredMinute] = preferredTime.split(':').map(Number);
      const preferredTimeMinutes = preferredHour * 60 + preferredMinute;
      const startTime = preferredTime;
      const endTime = calculateEndTime(startTime, smartBookingDuration);

      // Check if exact time is within business hours
      const [endHour, endMinute] = endTime.split(':').map(Number);
      const endTimeMinutes = endHour * 60 + endMinute;
      const [openHour, openMinute] = currentBusinessHours.openTime.split(':').map(Number);
      const [closeHour, closeMinute] = currentBusinessHours.closeTime.split(':').map(Number);
      const closeTimeMinutes = closeHour * 60 + closeMinute;

      if (endTimeMinutes <= closeTimeMinutes) {
        // Check all courts for availability at exact time
        const availableCourts = courts.filter(court => 
          !checkConflictsUsingCache(cachedSchedules, court.id, dateStr, startTime, endTime)
        ).map(court => ({
          courtId: court.id,
          courtName: court.name
        }));

        if (availableCourts.length > 0) {
          exactTimeRecommendations.push({
            startTime,
            endTime,
            availableCourts
          });
        }
      }

      // If we found an exact match, we don't need to look for alternatives
      if (exactTimeRecommendations.length === 0) {
        // Generate alternative time slots
        const timeSlots = [];
        for (let offset = -120; offset <= 120; offset += 30) {
          if (offset === 0) continue;

          const slotTimeMinutes = preferredTimeMinutes + offset;
          if (slotTimeMinutes < (openHour * 60 + openMinute) || slotTimeMinutes >= closeTimeMinutes) continue;

          const slotHour = Math.floor(slotTimeMinutes / 60);
          const slotMinute = slotTimeMinutes % 60;
          const altStartTime = `${slotHour.toString().padStart(2, '0')}:${slotMinute.toString().padStart(2, '0')}`;
          const altEndTime = calculateEndTime(altStartTime, smartBookingDuration);

          const [altEndHour, altEndMinute] = altEndTime.split(':').map(Number);
          const altEndTimeMinutes = altEndHour * 60 + altEndMinute;
          if (altEndTimeMinutes > closeTimeMinutes) continue;

          timeSlots.push({ altStartTime, altEndTime });
        }

        // Check each time slot for availability
        const availableCourtsMap = new Map<string, { courtId: string; courtName: string }[]>();

        timeSlots.forEach(({ altStartTime, altEndTime }) => {
          const availableCourts = courts.filter(court => 
            !checkConflictsUsingCache(cachedSchedules, court.id, dateStr, altStartTime, altEndTime)
          ).map(court => ({
            courtId: court.id,
            courtName: court.name
          }));

          if (availableCourts.length > 0) {
            availableCourtsMap.set(`${altStartTime}-${altEndTime}`, availableCourts);
          }
        });

        // Convert map to recommendations array
        const alternativeRecommendations = Array.from(availableCourtsMap.entries()).map(([timeKey, courts]) => {
          const [startTime, endTime] = timeKey.split('-');
          return {
            startTime,
            endTime,
            availableCourts: courts,
            startTimeMinutes: parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1])
          };
        }).sort((a, b) => {
          return Math.abs(a.startTimeMinutes! - preferredTimeMinutes) - Math.abs(b.startTimeMinutes! - preferredTimeMinutes);
        });

        recommendations.push(...alternativeRecommendations);
      }

      setRecommendations([...exactTimeRecommendations, ...recommendations]);
    } catch (error) {
      console.error('Error finding smart recommendations:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  // Add this component for pre-recommended time slots
  const PreRecommendedTimeSlots: React.FC<{
    onTimeSelect: (time: string) => void;
    businessHours: BusinessHours;
    date: Date;
  }> = ({ onTimeSelect, businessHours, date }) => {
    const dayOfWeek = format(date, 'EEEE').toLowerCase();
    const hours = businessHours[dayOfWeek];
    const queryClient = useQueryClient();
    const [showAll, setShowAll] = useState(false);

    if (hours.isClosed) return null;

    const [openHour, openMinute] = hours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);
    const openTimeMinutes = openHour * 60 + openMinute;
    const closeTimeMinutes = closeHour * 60 + closeMinute;

    // Get cached schedules from React Query
    const dateStr = format(date, 'yyyy-MM-dd');
    const facilityId = courts[0]?.facilityId;
    const cachedSchedules = facilityId ? queryClient.getQueryData<ScheduleSlot[]>(['schedules', facilityId, dateStr]) || [] : [];

    // Generate time slots in 30-minute increments and filter out those with conflicts
    const timeSlots = [];
    for (let minutes = openTimeMinutes; minutes < closeTimeMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const endTime = calculateEndTime(time, smartBookingDuration);

      // Check if any court is available at this time
      const hasAvailableCourt = courts.some(court => 
        !checkConflictsUsingCache(cachedSchedules, court.id, dateStr, time, endTime)
      );

      if (hasAvailableCourt) {
        timeSlots.push(time);
      }
    }

    if (timeSlots.length === 0) {
      return (
        <div className="text-sm text-gray-500">
          No available time slots found for this date.
        </div>
      );
    }

    const slotsToShow = showAll ? timeSlots : timeSlots.slice(0, 6);

    return (
      <div>
        <div className="flex flex-wrap gap-2 mb-4">
          {slotsToShow.map(time => (
            <button
              key={time}
              onClick={() => onTimeSelect(time)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              {formatTimeToAMPM(time)}
            </button>
          ))}
        </div>
        {timeSlots.length > 6 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-indigo-600 hover:underline text-sm px-2 py-1"
          >
            {showAll ? 'Show Less' : 'Show All'}
          </button>
        )}
      </div>
    );
  };

  if (!open) return null;

  return (
    <div className="scheduler-modal-overlay">
      <div className="scheduler-modal-content">
        <div className="scheduler-modal-header">
          <h2>{existingReservation ? 'Edit Reservation' : 'New Reservation'}</h2>
          <button className="scheduler-close-button" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b mb-4">
          {!existingReservation && (
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'smart'
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('smart')}
            >
              <SparklesIcon className="h-5 w-5 inline-block mr-2" />
              Smart Booking
            </button>
          )}
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'manual'
                ? 'border-b-2 border-indigo-500 text-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('manual')}
          >
            <CalendarIcon className="h-5 w-5 inline-block mr-2" />
            Manual Booking
          </button>
        </div>

        {activeTab === 'manual' ? (
          <form onSubmit={handleSubmit}>
            <div className="scheduler-form-group">
              <label>Court</label>
              <select
                className="scheduler-form-select"
                value={selectedCourt?.id || ''}
                onChange={(e) => {
                  const court = courts.find(c => c.id === e.target.value);
                  setSelectedCourt(court || null);
                }}
                required
              >
                <option value="">Select a court</option>
                {courts.map(court => (
                  <option key={court.id} value={court.id}>{court.name}</option>
                ))}
              </select>
            </div>

            <div className="scheduler-form-group">
              <label>Date</label>
              <DateSelector
                date={date}
                onDateChange={(newDate) => {
                  // Update the date in the parent component
                  if (onDateChange) {
                    onDateChange(newDate);
                  }
                }}
                businessHours={businessHours}
              />
            </div>

            {currentBusinessHours?.isClosed ? (
              <div className="text-center py-4">
                <p className="text-red-500">The facility is closed on {selectedDay}.</p>
                <p className="text-sm text-gray-500 mt-2">Please select a different date.</p>
              </div>
            ) : (
              <>
                <div className="scheduler-form-row">
                  <div className="scheduler-form-group">
                    <label>Start Time</label>
                    <select
                      className="scheduler-form-select"
                      value={manualStartTime}
                      onChange={(e) => handleManualTimeChange(e.target.value, 'start')}
                      required
                      disabled={!timeOptions.length}
                    >
                      <option value="">Select start time</option>
                      {timeOptions.map((time) => (
                        <option key={`start-${time}`} value={time}>
                          {formatTimeToAMPM(time)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="scheduler-form-group">
                    <label>End Time</label>
                    <select
                      className="scheduler-form-select"
                      value={manualEndTime}
                      onChange={(e) => handleManualTimeChange(e.target.value, 'end')}
                      required
                    >
                      <option value="">Select end time</option>
                      {timeOptions.map((time) => (
                        <option key={`end-${time}`} value={time}>
                          {formatTimeToAMPM(time)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="scheduler-form-group">
                  <label>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'available' | 'booked' | 'blocked')}
                    className="scheduler-form-select"
                  >
                    <option value="available">Available</option>
                    <option value="booked">Booked</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>

                {showAssignTo && (
                  <div className="scheduler-form-group">
                    <label>Assign To</label>
                    <select
                      value={selectedUserState}
                      onChange={(e) => setSelectedUserState(e.target.value)}
                      className="scheduler-form-select"
                    >
                      <option value="">Select User</option>
                      <optgroup label="Business Users">
                        {assignableUsers
                          .filter(u => u.type === 'business')
                          .map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))
                        }
                      </optgroup>
                      <optgroup label="Staff">
                        {assignableUsers
                          .filter(u => u.type === 'staff')
                          .map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))
                        }
                      </optgroup>
                      <optgroup label="Members">
                        {assignableUsers
                          .filter(u => u.type === 'member')
                          .map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))
                        }
                      </optgroup>
                    </select>
                  </div>
                )}

                <div className="scheduler-form-group">
                  <label>Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="scheduler-form-textarea"
                    placeholder="Add any additional notes..."
                  />
                </div>

                <div className="scheduler-form-group">
                  <label>Duration</label>
                  <select
                    className="scheduler-form-select"
                    value={currentDuration}
                    onChange={(e) => handleDurationChange(e.target.value as '30min' | '1hour')}
                  >
                    <option value="30min">30 minutes</option>
                    <option value="1hour">1 hour</option>
                  </select>
                </div>

                {conflict && conflict.conflictMessage && (
                  <div className="flex items-start bg-red-50 border-l-4 border-red-400 p-4 rounded-md mb-3">
                    <div className="flex-shrink-0 pt-0.5">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{conflict.conflictMessage}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="scheduler-modal-footer">
              {existingReservation && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="scheduler-button scheduler-button-danger"
                  disabled={loading}
                >
                  Delete
                </button>
              )}
              <div className="flex-grow"></div>
              <button
                type="button"
                onClick={onClose}
                className="scheduler-button scheduler-button-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="scheduler-button scheduler-button-primary"
                disabled={loading || !!conflict}
              >
                {loading ? 'Saving...' : existingReservation ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            {/* Replace the existing date navigation with the new DateSelector */}
            <DateSelector
              date={smartSelectedDate}
              onDateChange={setSmartSelectedDate}
              businessHours={businessHours}
            />

            {currentBusinessHours?.isClosed ? (
              <div className="text-center py-4">
                <p className="text-red-500">The facility is closed on {selectedDay}.</p>
                <p className="text-sm text-gray-500 mt-2">Please select a different date.</p>
              </div>
            ) : (
              <>
                <div className="scheduler-form-group">
                  <label>Duration</label>
                  <select
                    className="scheduler-form-select"
                    value={smartBookingDuration}
                    onChange={(e) => {
                      setSmartBookingDuration(e.target.value as '30min' | '1hour');
                      if (preferredTime) {
                        findSmartRecommendations(preferredTime, smartSelectedDate);
                      }
                    }}
                  >
                    <option value="30min">30 minutes</option>
                    <option value="1hour">1 hour</option>
                  </select>
                </div>

                <div className="scheduler-form-group">
                  <label>Quick Select Time</label>
                  <PreRecommendedTimeSlots
                    onTimeSelect={(time) => {
                      setPreferredTime(time);
                      findSmartRecommendations(time, smartSelectedDate);
                    }}
                    businessHours={businessHours}
                    date={smartSelectedDate}
                  />
                </div>

                <div className="scheduler-form-group">
                  <label>Preferred Time</label>
                  <select
                    className="scheduler-form-select"
                    value={preferredTime}
                    onChange={(e) => {
                      setPreferredTime(e.target.value);
                      findSmartRecommendations(e.target.value, smartSelectedDate);
                    }}
                    required
                    disabled={!timeOptions.length}
                  >
                    <option value="">Select preferred time</option>
                    {timeOptions.map((time) => (
                      <option key={`preferred-${time}`} value={time}>
                        {formatTimeToAMPM(time)}
                      </option>
                    ))}
                  </select>
                </div>

                {loadingRecommendations ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Finding available time slots...</p>
                  </div>
                ) : recommendations.length > 0 ? (
                  <div className="space-y-4">
                    {recommendations[0].startTime === preferredTime ? (
                      <h3 className="text-lg font-medium text-gray-900">Available at your preferred time</h3>
                    ) : (
                      <>
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-yellow-700">
                                Your preferred time ({formatTimeToAMPM(preferredTime)}) isn't available. Here are some alternative time slots:
                              </p>
                            </div>
                          </div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">Available Time Slots</h3>
                      </>
                    )}
                    <div className="grid grid-cols-1 gap-4">
                      {recommendations.slice(0, 5).map((rec, index) => (
                        <div
                          key={`${rec.startTime}-${rec.endTime}`}
                          className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:border-indigo-500 transition-colors"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-lg font-medium text-indigo-600">
                                {formatTimeToAMPM(rec.startTime)} - {formatTimeToAMPM(rec.endTime)}
                              </div>
                              <span className="text-sm text-gray-500">
                                {smartBookingDuration === '30min' ? '30 minutes' : '1 hour'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-500">
                                Available on {rec.availableCourts.length} court{rec.availableCourts.length > 1 ? 's' : ''}
                              </div>
                              <button
                                onClick={() => {
                                  // Show court selection modal
                                  setSelectedTimeSlot({
                                    startTime: rec.startTime,
                                    endTime: rec.endTime,
                                    availableCourts: rec.availableCourts
                                  });
                                  setShowCourtSelection(true);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                              >
                                Select Court
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : preferredTime ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No available time slots found for your preferred time.</p>
                    <p className="text-sm text-gray-400 mt-2">Try adjusting your preferred time or check back later.</p>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}

        {/* Court Selection Modal */}
        {showCourtSelection && selectedTimeSlot && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Select a Court for {formatTimeToAMPM(selectedTimeSlot.startTime)} - {formatTimeToAMPM(selectedTimeSlot.endTime)}
                </h3>

                {errorMessage && (
                  <div className="flex items-start bg-red-50 border-l-4 border-red-400 p-4 rounded-md mb-4">
                    <div className="flex-shrink-0 pt-0.5">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {selectedTimeSlot.availableCourts.map(court => (
                    <button
                      key={court.courtId}
                      onClick={async () => {
                        const selectedCourt = courts.find(c => c.id === court.courtId);
                        if (!selectedCourt) return;

                        setLoading(true);
                        setLoadingCourtId(court.courtId);
                        setErrorMessage(null);
                        try {
                          // Always include assigned_user_details: if no user is selected, use current business user
                          let selectedUserId = selectedUserState || user?.uid;
                          let selectedUserDetails = assignableUsers.find(u => u.id === selectedUserId);
                          if (!selectedUserDetails && user) {
                            selectedUserDetails = {
                              id: user.uid,
                              name: user.name || user.email || '',
                              email: user.email || '',
                              role: user.role || 'business',
                              type: 'business',
                            };
                          }
                          const reservationData = {
                            court_id: court.courtId,
                            date: format(activeTab === 'smart' ? smartSelectedDate : date, 'yyyy-MM-dd'),
                            start_time: selectedTimeSlot.startTime,
                            end_time: selectedTimeSlot.endTime,
                            status: 'booked',
                            notes: '',
                            assigned_to: selectedUserId,
                            assigned_user_details: selectedUserDetails,
                            exclude_event_id: existingReservation?.id,
                            created_at: new Date(),
                            updated_at: new Date()
                          };
                          console.log('Smart Reservation Data:', reservationData, {
                            smartSelectedDate,
                            date,
                            selectedTimeSlot,
                            activeTab
                          });

                          await reservationApi.createReservation(businessId, selectedCourt.facilityId, reservationData);
                          await onSave();
                          setShowCourtSelection(false);
                          onClose();
                        } catch (error: any) {
                          const apiError = ErrorHandler.handle(error);
                          let errorMsg = '';
                          if (apiError.error?.message) {
                            errorMsg = apiError.error.message;
                          } else if (apiError.error?.details) {
                            errorMsg = typeof apiError.error.details === 'string'
                              ? apiError.error.details
                              : JSON.stringify(apiError.error.details);
                          }

                          setErrorMessage(errorMsg);
                        } finally {
                          setLoading(false);
                          setLoadingCourtId(null);
                        }
                      }}
                      className="w-full text-left px-4 py-2 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-indigo-500 transition-colors relative"
                      disabled={loading}
                    >
                      <span>{court.courtName}</span>
                      {loading && loadingCourtId === court.courtId && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <LoadingSpinner size="sm" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setShowCourtSelection(false);
                      setErrorMessage(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewCourtReservationModal; 